import {
  ApolloClient,
  type ApolloLink,
  type DocumentNode,
  InMemoryCache,
  type NormalizedCacheObject,
} from '@apollo/client';
import { objectEntries } from 'tsafe';
import { sentry } from '~features/logging';
import {
  type StrictTypedTypePolicies,
  CompanyProfileCarouselDocument,
  CompanyProfileEmployerSegmentDocument,
  CompanyProfileListingDocument,
  CompanyProfilePublicDocument,
} from '../graphql/graphql';
import type { PrefetchedQueries } from './types';

const typePolicies: StrictTypedTypePolicies = {
  Metadata: {
    keyFields: ['startDate'],
  },
  CompanyProfileAds: {
    keyFields: false,
  },
  CompanyProfileQuery: {
    keyFields: [],
  },
  JobOfferOffer: {
    keyFields: ['ID'],
  },
  JobOfferCity: {
    keyFields: ['ID'],
  },
  JobOfferDistrict: {
    keyFields: ['ID'],
  },
};

type CreateApolloTokenOptions = {
  link: ApolloLink;
  initialCache?: NormalizedCacheObject;
  prefetchedQueries?: PrefetchedQueries;
};

const PREFETCHED_QUERY_MAP: Record<keyof PrefetchedQueries, DocumentNode> = {
  publicProfile: CompanyProfilePublicDocument,
  employerSegment: CompanyProfileEmployerSegmentDocument,
  profilesListing: CompanyProfileListingDocument,
  profilesCarousel: CompanyProfileCarouselDocument,
};

export const createApolloClient = ({
  link,
  initialCache,
  prefetchedQueries,
}: CreateApolloTokenOptions) => {
  let cache = new InMemoryCache({
    typePolicies,
  });

  if (initialCache) {
    cache = cache.restore(initialCache);
  } else if (prefetchedQueries) {
    objectEntries(prefetchedQueries).forEach(([name, value]) => {
      const query = PREFETCHED_QUERY_MAP[name];

      if (query && value) {
        const { data, variables } = value;

        try {
          cache.writeQuery({ query, data, variables });
        } catch (error) {
          sentry.captureException(error);
        }
      }
    });
  }

  return new ApolloClient({
    ssrMode: import.meta.env.MODE !== 'test' && import.meta.env.SSR === true,
    link,
    cache,
  });
};
